import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { GoogleSignInButton, AuthDivider } from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate(params.get("next") || "/app");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex flex-col lg:flex-row">
      {/* Left poster */}
      <div className="lg:w-1/2 bg-[#1A1F16] text-[#F7F5F0] p-10 lg:p-16 flex flex-col justify-between paper-grain">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A67C00]">Est. 2026 — Volume I</span>
          <h1 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight mt-8">
            The household<br/>ledger,<br/><span className="italic">re-imagined.</span>
          </h1>
          <p className="font-serif text-xl lg:text-2xl mt-10 leading-snug text-[#EAE7DD] max-w-md">
            Income. Expense. Demat. Budget. <br/>
            One paper-quiet dashboard for every rupee that moves through your home.
          </p>
        </div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565]">
          ₹ · Lakhs · Crores · Indian numbering, native.
        </div>
      </div>

      {/* Right form */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Sign in</span>
          <h2 className="font-serif text-4xl lg:text-5xl mt-2 mb-10 tracking-tight">Open your ledger</h2>

          <label className="block mb-6">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-2">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full bg-transparent border-0 border-b border-[#1A1F16] py-2 text-lg focus:outline-none focus:border-[#A67C00] num"
              placeholder="you@home.in"
            />
          </label>

          <label className="block mb-4">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-2">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full bg-transparent border-0 border-b border-[#1A1F16] py-2 text-lg focus:outline-none focus:border-[#A67C00]"
              placeholder="••••••••"
            />
          </label>

          <div className="mb-10 text-right">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] hover:text-[#A67C00] transition-colors">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full bg-[#1A1F16] text-[#F7F5F0] py-4 uppercase tracking-[0.25em] text-xs hover:bg-[#2C3628] disabled:opacity-50 transition-colors"
          >
            {busy ? "Opening…" : "Sign in"}
          </button>

          <AuthDivider />

          <GoogleSignInButton />

          <div className="mt-8 ledger-rule h-px" />
          <p className="mt-8 text-sm text-[#6C7565]">New here?</p>
          <Link
            to="/register"
            data-testid="login-signup-btn"
            className="mt-3 block w-full text-center border border-[#1A1F16] text-[#1A1F16] py-4 uppercase tracking-[0.25em] text-xs hover:bg-[#A67C00] hover:border-[#A67C00] hover:text-white transition-colors"
          >
            Sign up — it's free
          </Link>
        </form>
      </div>
    </div>
  );
}
